// authentication.js - Handles wallet connections and authentication

import store from './store.js';
import { apiUrl } from './api-settings.js';
import { showNotification } from './utils.js';
import { getDeviceId } from './utils.js';
import { hasAcceptedDisclaimer } from './ui.js';
import { hideAllModals, onSuccessfulAuth } from '../main.js';
import { setupGoogleAuth } from './google-auth.js';

// List of supported wallet adapters
const SUPPORTED_WALLETS = [
    {
        name: "Phantom",
        icon: "assets/icons/phantom.svg",
        url: "https://phantom.app/",
        adapter: window.solana,
    },
    {
        name: "Solflare",
        icon: "assets/icons/solflare.avif",
        url: "https://solflare.com/",
        getAdapter: () => window.solflare,
        detectFn: () => {
            return (
                window.solflare &&
                (window.solflare.isSolflare || window.solflare.isConnected)
            );
        },
    },
    {
        name: "Backpack",
        icon: "assets/icons/backpack.png",
        url: "https://backpack.app/",
        adapter: window.backpack,
    },
    {
        name: "Brave Wallet",
        icon: "assets/icons/brave.png",
        url: "https://brave.com/wallet/",
        adapter: window.braveSolana,
    },
    {
        name: "Glow",
        icon: "assets/icons/glow.png",
        url: "https://glow.app/",
        adapter: window.glow,
    },
    {
        name: "Pipeline Wallet",
        icon: "assets/icons/pipeline.png",
        url: "https://wallet.ppline.app/",
        detectFn: () => !!window.pipeline,
        adapter: window.pipeline
    }
];

/**
 * Sets up authentication UI and event listeners
 */
function setupAuthentication() {
    // Initialize Google authentication
    setupGoogleAuth();
}

/**
 * Shows the wallet selection modal for connecting wallets
 */
function showWalletModal() {

    const walletModal = document.getElementById("walletModal");
    const walletList = document.getElementById("walletList");
    const loginModal = document.getElementById("loginModal");

    try {
        if (walletList) {
            walletList.innerHTML = "";

            // Prepare wallet list with status
            const walletsWithStatus = SUPPORTED_WALLETS.map(wallet => {
                let isAvailable = wallet.detectFn
                    ? wallet.detectFn()
                    : !!wallet.adapter;

                let isConnected = false;
                if (isAvailable) {
                    if (wallet.adapter && typeof wallet.adapter.isConnected === "boolean") {
                        isConnected = wallet.adapter.isConnected;
                    } else if (wallet.adapter && typeof wallet.adapter.connected === "boolean") {
                        isConnected = wallet.adapter.connected;
                    } else if (wallet.adapter && wallet.adapter.publicKey) {
                        isConnected = true; // Assume connected if publicKey exists and other checks fail
                    }
                }
                return { wallet, isAvailable, isConnected };
            });

            // Sort wallets: Connected first, then available, then by name
            walletsWithStatus.sort((a, b) => {
                // Pipeline Wallet is always first regardless of connection status
                if (a.wallet.name === "Pipeline Wallet") return -1;
                if (b.wallet.name === "Pipeline Wallet") return 1;
                
                // Standard sorting: Connected first
                if (a.isConnected !== b.isConnected) {
                    return a.isConnected ? -1 : 1; // Connected first
                }
                if (a.isAvailable !== b.isAvailable) {
                    return a.isAvailable ? -1 : 1; // Available second
                }
                return a.wallet.name.localeCompare(b.wallet.name); // Alphabetical otherwise
            });

            // Create and append buttons from sorted list
            walletsWithStatus.forEach(({ wallet, isAvailable, isConnected }) => {
                const walletButton = document.createElement("button");
                walletButton.className = "wallet-option";

                walletButton.innerHTML = `
                    <div class="wallet-option-content">
                        <img src="${wallet.icon}" alt="${
                    wallet.name
                }" onerror="this.onerror=null; this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHJ4PSI4IiBmaWxsPSIjRUVFIi8+PHBhdGggZD0iTTggOEg3VjlIOFY4WiIgZmlsbD0iIzMzMyIvPjxwYXRoIGQ9Ik0xNiA4SDE1VjlIMTZWOFoiIGZpbGw9IiMzMzMiLz48cGF0aCBkPSJNOSAxM0g4VjE0SDlWMTNaIiBmaWxsPSIjMzMzIi8+PHBhdGggZD0iTTE1IDEzSDE0VjE0SDE1VjEzWiIgZmlsbD0iIzMzMyIvPjxwYXRoIGQ9Ik05IDE2SDhWMTdIOVYxNloiIGZpbGw9IiMzMzMiLz48cGF0aCBkPSJNMTUgMTZIMTRWMTdIMTVWMTZaIiBmaWxsPSIjMzMzIi8+PHBhdGggZD0iTTEyIDE2SDExVjE3SDEyVjE2WiIgZmlsbD0iIzMzMyIvPjwvc3ZnPg==';">
                        <span class="wallet-option-name">${wallet.name}</span>
                    </div>
                    <span class="wallet-option-status ${
                        isAvailable
                            ? isConnected
                                ? "connected"
                                : "installed"
                            : "not-installed"
                    }">
                        ${isAvailable ? "Connect" : "Install"}
                    </span>
                `;

                walletButton.addEventListener("click", (e) => {
                    e.preventDefault();
                    try {
                        hideWalletModal();
                        if (isAvailable) {
                            connectWallet(wallet);
                        } else {
                            window.open(wallet.url, "_blank");
                        }
                    } catch (err) {
                        console.error("Error when clicking wallet button:", err);
                        showNotification(`Error: ${err.message}`, "error");
                    }
                });

                walletList.appendChild(walletButton);
            });
        }

        if (walletModal && loginModal) {
            loginModal.style.display = "none";
            walletModal.style.display = "flex";
        }
    } catch (err) {
        console.error("Error showing wallet modal:", err);
        showNotification(
            `Error showing wallet options: ${err.message}`,
            "error"
        );
    }
}

/**
 * Hides the wallet selection modal
 */
function hideWalletModal() {
    try {
        const walletModal = document.getElementById("walletModal");
        const loginModal = document.getElementById("loginModal");

        if (walletModal) {
            walletModal.style.display = "none";

            if (!store.getIsAuthenticated() && loginModal) {
                loginModal.style.display = "flex";
            }
        }
    } catch (err) {
        console.error("Error hiding wallet modal:", err);
    }
}

/**
 * Shows the learn more modal
 */
function showLearnMore() {
    const loginModal = document.getElementById("loginModal");
    const learnMoreModal = document.getElementById("learnMoreModal");
    
    if (loginModal) loginModal.style.display = "none";
    if (learnMoreModal) learnMoreModal.style.display = "flex";
}

/**
 * Hides the learn more modal
 */
function hideLearnMore() {
    const learnMoreModal = document.getElementById("learnMoreModal");
    const loginModal = document.getElementById("loginModal");
    
    if (learnMoreModal) learnMoreModal.style.display = "none";
    if (loginModal) loginModal.style.display = "flex";
}

/**
 * Hides the limit reached modal
 */
function hideLimitReached() {
    const limitReachedModal = document.getElementById("limitReachedModal");
    if (limitReachedModal) {
        limitReachedModal.style.display = "none";
    }
}

/**
 * Shows the logout confirmation modal
 */
function showLogoutConfirmation() {
    const logoutModal = document.getElementById("logoutModal");
    if (logoutModal) {
        logoutModal.style.display = "flex";
    }
}

/**
 * Hides the logout confirmation modal
 */
function hideLogoutConfirmation() {
    const logoutModal = document.getElementById("logoutModal");
    if (logoutModal) {
        logoutModal.style.display = "none";
    }
}

/**
 * Performs the logout action, clearing state and showing the login screen
 */
function performLogout() {
    store.setAuthenticated(false);
    store.setGuestMode(false);
    store.setWalletAddress(null);
    store.setConversations([]);
    store.setCurrentConversationId(null);
    
    // Remove Google badge if present
    const googleBadge = document.querySelector('.google-badge');
    if (googleBadge) {
        googleBadge.remove();
    }

    // Hide logout modal and app container, show login modal
    const logoutModal = document.getElementById("logoutModal");
    const appContainer = document.getElementById("appContainer");
    const loginModal = document.getElementById("loginModal");
    const messagesContainer = document.getElementById("messagesContainer");
    const conversationsList = document.getElementById("conversationsList");
    const messageInput = document.getElementById("messageInput");
    const connectWalletBtn = document.getElementById("connectWalletBtn");

    if (logoutModal) logoutModal.style.display = "none";
    if (appContainer) appContainer.style.display = "none";
    if (loginModal) loginModal.style.display = "flex";
    
    // Clear UI elements
    if (messagesContainer) messagesContainer.innerHTML = "";
    if (conversationsList) conversationsList.innerHTML = "";
    if (messageInput) messageInput.value = "";
    
    
    // Clear authentication token
    localStorage.removeItem("quantumAuthToken");
}

function connectPipelineWallet() {
    return new Promise((resolve, reject) => {
        if (!window.pipeline) {
            return reject(new Error("Pipeline Wallet is not installed"));
        }

        const listener = (e) => {
            window.removeEventListener('connectToSite', listener); // Clean up listener

            if (e.detail && e.detail.connectedWallet) {
                 const address = e.detail.connectedWallet;

                resolve(address); // Resolve promise with the address
            } else {
                reject(new Error("Connection failed or cancelled."));
            }
        };

        window.addEventListener('connectToSite', listener);

        try {
            window.pipeline.connect(); // Initiate connection
        } catch (err) {
            window.removeEventListener('connectToSite', listener); // Clean up listener on error
            reject(err); // Reject if connect() throws immediately
        }
    });
}

/**
 * Connects to a selected wallet
 * @param {Object} wallet - The wallet configuration object
 */
async function connectWallet(wallet) {
    try {
        const loadingNotification = showNotification(
            `Connecting to ${wallet.name}...`,
            "info"
        );

        let isAvailable = wallet.detectFn
            ? wallet.detectFn()
            : wallet.getAdapter
            ? !!wallet.getAdapter()
            : !!wallet.adapter;

        if (!isAvailable) {
            showNotification(`${wallet.name} is not installed`, "error");
            setTimeout(() => {
                window.open(wallet.url, "_blank");
            }, 1000);
            return;
        }

        let walletAddress = "";
        const deviceId = getDeviceId();

        // --- Pipeline Specific Logic ---
        if (wallet.name === "Pipeline Wallet") {
            try {
                walletAddress = await connectPipelineWallet(); // Use a helper function for clarity
                if (!walletAddress) return; // Connection failed or was rejected

                // Call the specific verification endpoint for Pipeline
                const verificationResponse = await fetch(
                    `${apiUrl}/verify-pipeline-wallet`, // <-- Replace with your actual endpoint
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            walletAddress: walletAddress,
                            deviceId: deviceId,
                        }),
                    }
                );

                const verificationData = await verificationResponse.json();

                if (!verificationResponse.ok) {
                    throw new Error(verificationData.error || "Pipeline wallet verification failed");
                }

                // Common success logic (after verification)
                store.setAuthenticated(true);
                store.setWalletAddress(walletAddress);
                // Assuming the endpoint returns a token and maybe balance info
                localStorage.setItem("quantumAuthToken", verificationData.token);
                if (verificationData.tokenBalance) {
                     localStorage.setItem("laneTokens", JSON.stringify(verificationData.tokenBalance));
                }
                 // No specific listener setup needed like for Solana wallets? Add if required.

                hideAllModals();
                if (hasAcceptedDisclaimer()) {
                    onSuccessfulAuth();
                } else {
                    showDisclaimerModal();
                }
                showNotification("Pipeline Wallet connected successfully!", "success");

            } catch (error) {
                 showNotification(`Error connecting Pipeline: ${error.message}`, "error");
                 console.error("Pipeline connection error:", error); // Log error details
            }
            return; // Exit after handling Pipeline
        }

        // --- Existing Logic for Solana Wallets ---
        const adapter = wallet.getAdapter ? wallet.getAdapter() : wallet.adapter;

        if (!adapter) {
            showNotification(
                `${wallet.name} adapter not found. Please refresh the page and try again.`,
                "error"
            );
            return;
        }
        
        try {
            const response = await adapter.connect();

            if (response && response.publicKey) {
                walletAddress =
                    typeof response.publicKey.toString === "function"
                        ? response.publicKey.toString()
                        : response.publicKey;
            } else if (adapter.publicKey) {
                walletAddress =
                    typeof adapter.publicKey.toString === "function"
                        ? adapter.publicKey.toString()
                        : adapter.publicKey;
            } else if (response && response.address) {
                walletAddress = response.address;
            } else if (response === true && wallet.name === "Solflare" && adapter.publicKey) {
                // Special case for Solflare which may return true on successful connection
                walletAddress = adapter.publicKey.toString();
            } else {
                if (response) {
                    console.log("Unusual wallet response:", response);
                    walletAddress = JSON.stringify(response).substring(0, 50);
                } else if (adapter.publicKey) {
                    walletAddress = adapter.publicKey;
                } else {
                    throw new Error("Could not retrieve wallet address");
                }
            }

            if (typeof walletAddress !== "string" || !walletAddress) {
                if (wallet.adapter.publicKey) {
                    walletAddress = wallet.adapter.publicKey.toString();
                } else {
                    throw new Error("Could not determine wallet address");
                }
            }
        } catch (err) {
            showNotification(
                `Error connecting to ${wallet.name}: ${err.message || 'Connection cancelled or failed'}`,
                "error"
            );
            return;
        }

        // --- Existing Nonce/Signature/Verification Logic for Solana ---
        try {


            const nonceResponse = await fetch(
                `${apiUrl}/get-nonce?walletAddress=${encodeURIComponent(
                    walletAddress
                )}`
            );

            if (!nonceResponse.ok) {
                throw new Error(`Failed to get nonce: ${nonceResponse.status}`);
            }

            const nonceData = await nonceResponse.json();
            const nonce = nonceData.nonce;
            const message = `Connection to Quantum: ${nonce}`;
            const encodedMessage = new TextEncoder().encode(message);

            let signature;
            try {
                const signatureResponse = await adapter.signMessage(
                    encodedMessage,
                    "utf8"
                );

                signature = signatureResponse.signature || signatureResponse;
            } catch (err) {
                showNotification(
                    `Error signing message: ${err.message}`,
                    "error"
                );
                return;
            }

            if (!signature) {
                throw new Error("Signature failed");
            }

            let signatureBase64;
            try {
                if (signature instanceof Uint8Array) {
                    signatureBase64 = btoa(
                        String.fromCharCode.apply(null, signature)
                    );
                } else if (typeof signature === "string") {
                    signatureBase64 = btoa(signature);
                } else {
                    signatureBase64 = btoa(
                        String.fromCharCode.apply(null, Object.values(signature))
                    );
                }
            } catch (err) {
                signatureBase64 = btoa(JSON.stringify(signature));
            }

            const verificationResponse = await fetch(
                `${apiUrl}/verify-wallet`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        walletAddress: walletAddress,
                        nonce: nonce,
                        signature: signatureBase64,
                        message: message,
                        deviceId: deviceId,
                    }),
                }
            );

            const verificationData = await verificationResponse.json();

            if (!verificationResponse.ok) {
                throw new Error(verificationData.error || "Verification failed");
            }

            store.setAuthenticated(true);
            store.setWalletAddress(walletAddress);

            localStorage.setItem("quantumAuthToken", verificationData.token);
            localStorage.setItem("laneTokens", JSON.stringify(verificationData.tokenBalance));

            setupWalletChangeListener(wallet);

            hideAllModals();
            
            if (hasAcceptedDisclaimer()) {
                onSuccessfulAuth();
            } else {
                showDisclaimerModal();
            }

            showNotification("Wallet connected successfully!", "success");
        } catch (error) {
            showNotification(`Error: ${error.message}`, "error");
        }
    } catch (error) {
        showNotification(`Error: ${error.message}`, "error");
    }
}

/**
 * Sets up listeners for wallet disconnection or account changes
 * @param {Object} wallet - The wallet configuration object
 */
function setupWalletChangeListener(wallet) {
    try {
        if (wallet.adapter && typeof wallet.adapter.on === "function") {
            wallet.adapter.on("disconnect", () => {
                console.log("Wallet disconnected");
                performLogout();
            });

            if (typeof wallet.adapter.on === "function") {
                wallet.adapter.on("accountChanged", () => {
                    console.log("Wallet account changed");
                    showNotification(
                        "Wallet account changed. Please reconnect.",
                        "info"
                    );
                    performLogout();
                });
            }
        }

        console.log("Wallet change listener set up successfully");
    } catch (error) {
        console.error("Error setting up wallet change listener:", error);
    }
}

/**
 * Shows the disclaimer modal for first-time users
 */
function showDisclaimerModal() {
    const disclaimerModal = document.getElementById("disclaimerModal");
    if (disclaimerModal) {
        disclaimerModal.style.display = "flex";
    }
}

/**
 * Hides the disclaimer modal
 */
function hideDisclaimerModal() {
    const disclaimerModal = document.getElementById("disclaimerModal");
    if (disclaimerModal) {
        disclaimerModal.style.display = "none";
    }
}

/**
 * Checks if the device is mobile
 * @returns {boolean} True if the device is mobile
 */
function isMobileDevice() {
    return (
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )
    );
}

// Export functions and variables
export {
    setupAuthentication,
    showWalletModal,
    hideWalletModal,
    showLearnMore,
    hideLearnMore,
    hideLimitReached,
    showLogoutConfirmation,
    hideLogoutConfirmation,
    performLogout,
    connectWallet,
    setupWalletChangeListener,
    showDisclaimerModal,
    hideDisclaimerModal,
    isMobileDevice,
    SUPPORTED_WALLETS
};